import { useMemo, useState } from 'react';
import { useTicketDetailsParams } from '../hooks/useTicketDetailsParams';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Tabs, Tab } from 'foundations-components/transitional/components/Tabs';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TicketInfoGrid } from './TicketInfo/TicketInfoGrid';
import { CloseTicket } from './CloseTicket';
import { MessageAttachment } from './MessageAttachment';
import { NoAttachments } from './NoAttachments';
import { OverlayPanel } from './OverlayPanel';
import { SidebarPanel } from './SidebarPanel';
import { useTicket } from '../data/useTicket';
import { useConfiguredTicketProperties } from '../data/usePortalData';
import { useThreadAttachments } from '../data/useThreadAttachments';
import { useIsMobile } from '../hooks/useBreakpoint';
import { sortConfiguredTicketProperties } from '../utils/sortConfiguredTicketProperties';
import { MAX_ATTACHMENTS_PER_THREAD } from '../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function DetailsTabContent({
  ticketId
}) {
  const configuredTicketProperties = useConfiguredTicketProperties();
  const ticketData = useTicket(ticketId);
  const configuredTicketPropertiesToDisplay = useMemo(() => {
    if (!configuredTicketProperties) return [];
    return sortConfiguredTicketProperties(configuredTicketProperties).filter(({
      propertyName
    }) => propertyName !== 'subject' && propertyName !== 'content');
  }, [configuredTicketProperties]);
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "500",
    flex: "1",
    minInlineSize: "0",
    children: [/*#__PURE__*/_jsx(TicketInfoGrid, {
      ticketId: ticketId,
      configuredTicketProperties: configuredTicketPropertiesToDisplay,
      loading: ticketData.loading,
      error: !!ticketData.error,
      refetchTicket: ticketData.refetch
    }), /*#__PURE__*/_jsx(CloseTicket, {
      ticket: ticketData.data,
      ticketId: ticketId
    })]
  });
}
function AttachmentsTabContent({
  attachments,
  isLoading
}) {
  const loadedMaxAttachments = attachments.length === MAX_ATTACHMENTS_PER_THREAD;
  if (isLoading && attachments.length === 0) {
    return /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "400",
      alignItems: "center",
      justifyContent: "center",
      marginBlockStart: "800",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {})
    });
  }
  if (attachments.length === 0) {
    return /*#__PURE__*/_jsx(NoAttachments, {});
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "400",
    flex: "1",
    inlineSize: "100%",
    maxInlineSize: "100%",
    minInlineSize: "0",
    children: [attachments.map(attachment => /*#__PURE__*/_jsx(MessageAttachment, {
      attachment: attachment.attachment,
      sensitiveAttachment: attachment.sensitiveAttachment,
      customSubtitle: attachment.sentDate ? I18n.moment.userTz(attachment.sentDate).format('LL') : null
    }, attachment.fileId)), loadedMaxAttachments && /*#__PURE__*/_jsx(Text, {
      as: "p",
      typography: "detail-200",
      textAlign: "center",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.attachmentsPanel.displayingMaxAttachments",
        options: {
          MAX_ATTACHMENTS_PER_THREAD
        }
      })
    })]
  });
}
export function TicketDetailsPanel({
  showAsOverlay,
  isOpen,
  setIsOpen
}) {
  const [selectedTab, setSelectedTab] = useState('details');
  const isMobile = useIsMobile();
  const {
    ticketId,
    threadId
  } = useTicketDetailsParams();
  const {
    data: threadAttachments,
    loading: attachmentsLoading
  } = useThreadAttachments(ticketId, threadId);
  const combinedAttachments = useMemo(() => {
    if (!threadAttachments) return [];
    const {
      attachmentsList,
      sensitiveAttachmentsList
    } = threadAttachments;
    return [...attachmentsList.map((attachment, i) => ({
      fileId: `attachment-${i}`,
      sensitiveAttachment: undefined,
      attachment,
      sentDate: attachment.attachmentTimestamp
    })), ...sensitiveAttachmentsList.map((sensitiveAttachment, i) => ({
      fileId: `sensitive-attachment-${i}`,
      sensitiveAttachment,
      attachment: undefined,
      sentDate: undefined
    }))].sort((a, b) => (a.sentDate || Infinity) - (b.sentDate || Infinity));
  }, [threadAttachments]);
  const handleClose = () => {
    setSelectedTab('details');
    setIsOpen(false);
  };
  const title = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customer-portal-app.ticketDetailsPanel.title"
  });
  const tabs = /*#__PURE__*/_jsxs(Tabs, {
    selected: selectedTab,
    onSelectedChange: e => setSelectedTab(e.target.value),
    bordered: true,
    fullHeightPanel: true,
    panelStyles: {
      display: 'flex',
      flexDirection: 'column',
      paddingBlockStart: '700',
      paddingBlockEnd: '0',
      paddingInline: '0'
    },
    children: [/*#__PURE__*/_jsx(Tab, {
      tabId: "details",
      title: I18n.text('customer-portal-app.ticketDetailsPanel.tabs.details'),
      children: /*#__PURE__*/_jsx(DetailsTabContent, {
        ticketId: ticketId
      })
    }), /*#__PURE__*/_jsx(Tab, {
      tabId: "attachments",
      title: I18n.text('customer-portal-app.ticketDetailsPanel.tabs.attachments'),
      children: /*#__PURE__*/_jsx(AttachmentsTabContent, {
        attachments: combinedAttachments,
        isLoading: attachmentsLoading
      })
    })]
  });
  if (showAsOverlay) {
    return /*#__PURE__*/_jsx(OverlayPanel, {
      isOpen: isOpen,
      onClose: handleClose,
      align: "right",
      inlineSize: isMobile ? '100dvw' : '435px',
      ariaCloseLabel: I18n.text('customer-portal-app.ticketDetailsPanel.ariaClose'),
      title: title,
      children: tabs
    });
  }
  return /*#__PURE__*/_jsx(SidebarPanel, {
    borderSide: "start",
    iconRotated: true,
    expandAriaLabel: I18n.text('customer-portal-app.ticketDetailsPanel.expand'),
    collapseAriaLabel: I18n.text('customer-portal-app.ticketDetailsPanel.collapse'),
    title: title,
    children: tabs
  });
}